import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPost, publishPost } from '../api/posts';
import { Post } from '../types';
import StatusMessage from '../components/StatusMessage';
import MarkdownEditor from '../components/common/MarkdownEditor';
import Button from '../components/common/Button';
import BookPage from '../components/common/BookPage';
import Card from '../components/common/Card';
import Input from '../components/common/Input';

const postSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title must be less than 200 characters'),
  content: z.string().min(1, 'Content is required'),
});

type PostFormData = z.infer<typeof postSchema>;

const CreatePostPage: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [post, setPost] = useState<Post | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<PostFormData>({
    resolver: zodResolver(postSchema),
    defaultValues: {
      title: '',
      content: '',
    },
  });

  const createPostMutation = useMutation({
    mutationFn: async (data: PostFormData) => {
      if (!token) throw new Error('No authentication token available');
      return createPost(
        {
          title: data.title,
          content: data.content,
          excerpt: data.content.length > 200 ? data.content.slice(0, 200) : data.content,
        },
        token,
      );
    },
    onSuccess: (data) => {
      setPost(data);
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'posts'] });
    },
    onError: (mutationError) => {
      setError(mutationError instanceof Error ? mutationError.message : 'Failed to create post');
    },
  });

  const publishPostMutation = useMutation({
    mutationFn: async (postId: string) => {
      if (!token) throw new Error('No authentication token available');
      return publishPost(postId, token);
    },
    onSuccess: (data) => {
      setPost(data);
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts', 'detail', data.id] });
    },
    onError: (mutationError) => {
      setError(mutationError instanceof Error ? mutationError.message : 'Failed to publish post');
    },
  });

  if (!user && !token) {
    return (
      <div className="min-h-screen pt-16 md:pt-20">
        <section className="px-4 py-16 text-center">
          <StatusMessage type="loading" className="mx-auto max-w-sm" />
        </section>
      </div>
    );
  }

  if (!user || user.user_role !== 'admin') {
    return (
      <div className="min-h-screen pt-16 md:pt-20">
        <section className="mx-auto max-w-3xl px-4 py-16">
          <Card className="text-center">
            <h2 className="font-serif text-3xl text-[#2c201b]">Writers' room only.</h2>
            <p className="mt-3 text-[#5b5048]">You must be an admin to create a post.</p>
          </Card>
        </section>
      </div>
    );
  }

  if (post) {
    return (
      <BookPage
        eyebrow="Publishing"
        title="Post created."
        intro="Review the details below, publish when you're ready, or head back to the admin list."
        bodyClassName="mx-auto max-w-4xl space-y-6"
        headerAside={
          <div className="max-w-xs border border-[color:var(--wf-border)] bg-[rgba(255,255,255,0.18)] px-4 py-3">
            Drafts stay private until you publish them.
          </div>
        }
      >
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Title</p>
          <h2 className="mt-2 font-serif text-3xl text-[color:var(--wf-ink)]">{post.title}</h2>
        </div>
        <dl className="grid gap-4 sm:grid-cols-2">
          <div className="border border-[color:var(--wf-border)] bg-[rgba(255,255,255,0.18)] p-4">
            <dt className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Status</dt>
            <dd className="mt-2 text-[color:var(--wf-ink)]">{post.status}</dd>
          </div>
          <div className="border border-[color:var(--wf-border)] bg-[rgba(255,255,255,0.18)] p-4">
            <dt className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Slug</dt>
            <dd className="mt-2 break-all font-mono text-sm text-[color:var(--wf-plum)]">{post.slug}</dd>
          </div>
        </dl>
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Excerpt</p>
          <p className="mt-2 border border-[color:var(--wf-border)] bg-[rgba(255,255,255,0.18)] p-4 text-[color:var(--wf-ink)]">
            {post.excerpt}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Content</p>
          <pre className="mt-2 whitespace-pre-wrap border border-[color:var(--wf-border)] bg-[rgba(255,255,255,0.18)] p-5 text-sm leading-7 text-[color:var(--wf-ink)]">
            {post.content}
          </pre>
        </div>
        <div className="flex flex-wrap gap-3 border-t border-[#8f7e69]/15 pt-4">
          {post.status === 'draft' ? (
            <Button onClick={() => publishPostMutation.mutate(post.id)} isLoading={publishPostMutation.isPending}>
              Publish post
            </Button>
          ) : (
            <Button type="button" onClick={() => navigate(`/blog/${post.id}`)}>
              View published post
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={() => navigate('/admin/posts')}>
            View all posts
          </Button>
        </div>
        {publishPostMutation.isError && (
          <div className="rounded-[0.8rem] border border-red-200 bg-red-50/80 p-3 text-sm text-red-700">
            {publishPostMutation.error instanceof Error ? publishPostMutation.error.message : 'Failed to publish post'}
          </div>
        )}
      </BookPage>
    );
  }

  const contentPreview = watch('content')?.slice(0, 200) || 'Your excerpt will appear here once you start writing.';

  return (
    <BookPage
      eyebrow="Publishing"
      title="Write a new post."
      intro="Draft in markdown, review the excerpt, and publish only when the piece is ready to meet the world."
      bodyClassName="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]"
      maxWidthClassName="max-w-6xl"
    >
      <div className="space-y-6">
        <form onSubmit={handleSubmit((data) => createPostMutation.mutate(data))} className="space-y-6">
          <Input id="title" label="Title" placeholder="Enter post title" error={errors.title?.message} {...register('title')} />
          <div>
            <div className="mb-2 flex items-center justify-between gap-4">
              <label htmlFor="content" className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">
                Content
              </label>
              <span className="text-xs text-[color:var(--wf-plum)]">Markdown supported</span>
            </div>
            <MarkdownEditor
              value={watch('content') || ''}
              onChange={(value) => setValue('content', value || '', { shouldValidate: true })}
              placeholder="Write your post content here..."
            />
            {errors.content && <p className="mt-2 text-sm text-red-600">{errors.content.message}</p>}
          </div>
          {error && <div className="rounded-[0.8rem] border border-red-200 bg-red-50/80 p-3 text-sm text-red-700">{error}</div>}
          <Button type="submit" fullWidth size="large" isLoading={createPostMutation.isPending}>
            Create post
          </Button>
        </form>
      </div>

      <aside className="space-y-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Excerpt preview</p>
          <p className="mt-3 border border-[color:var(--wf-border)] bg-[rgba(255,255,255,0.18)] p-4 text-sm leading-7 text-[color:var(--wf-ink)]">
            {contentPreview}
          </p>
        </div>
        <div className="border border-[color:var(--wf-border)] bg-[rgba(255,255,255,0.18)] p-4 text-sm leading-7 text-[color:var(--wf-ink-soft)]">
          Keep titles concise, lead with a clear first paragraph, and let the excerpt invite readers deeper.
        </div>
      </aside>
    </BookPage>
  );
};

export default CreatePostPage;
